/**
 * Types for the new-engine ("v2") reports, e.g. the Daily Sales Report.
 *
 * A v2 report is configured by a saved *view* (`SalonReport.view_config`) and
 * run through `POST /business-locations/{salon_id}/reports/{report_id}/v2/run`, which
 * returns a grouped tree of metric values. Everything the canvas shows about a
 * metric (label, section, format, description, formula, availability) comes
 * from the definition / run response — no metric copy lives in the portal.
 *
 * Mirrors the reports service (reports_utils/v2: serialization.py,
 * view_config.py, tree.py and the run_report_v2 handler).
 */
import { BucketGranularity } from "../utils/dateRangeFormat";
import { MetricFormat } from "../utils/metricFormat";
import {
  DateRangePreset,
  ReportColumn,
  ReportV2DefinitionSummary,
  SalonReport,
} from "./reports";

/** Saved-view schema version the portal reads and writes. */
export const VIEW_CONFIG_VERSION = 1;

export type ReportGranularity = BucketGranularity;
export type ViewDensity = "comfortable" | "compact";
export type SortDirection = "asc" | "desc";

/** One column / summary total a v2 report can show. */
export interface ReportMetricDef {
  key: string;
  label: string;
  /** Key of the `metric_sections` entry it is listed under. */
  section: string;
  format: MetricFormat;
  /** Plain-language explanation (tooltip). */
  description: string;
  /** Human-readable formula using metric names (tooltip). */
  formula: string;
  /** False while the data isn't tracked; its values are always null. */
  available: boolean;
  unavailable_reason: string | null;
  /** False for distinct counts and percents: never add child values up. */
  summable: boolean;
}

export interface ReportMetricSection {
  key: string;
  label: string;
}

/** A field rows can be grouped or filtered by. */
export interface ReportDimensionDef {
  key: string;
  label: string;
  /** Label the API uses for rows with no value ("Unassigned"). */
  empty_label: string;
}

export interface ReportFilterDef extends ReportDimensionDef {
  /** Always visible in the toolbar (can't be removed). */
  always_shown: boolean;
}

/** Metrics expanded per run, e.g. one "Total amount collected ({method})"
 *  column per payment method (`collected__cash`, `collected__custom:Venmo`…). */
export interface DynamicMetricGroup {
  key_prefix: string;
  section: string;
  /** The static metric the expanded metrics follow in display order. */
  anchor_after: string;
  label_template: string;
}

export interface ViewRange {
  preset: DateRangePreset;
  /** "YYYY-MM-DD"; only with `preset: "fixed_dates"`. */
  start?: string;
  end?: string;
}

export interface ViewSort {
  /** A metric key, or "label" for the first (group label) column. */
  key: string;
  dir: SortDirection;
}

/** A v2 report's saved view (`salon_reports.view_config`). */
export interface ViewConfig {
  v: typeof VIEW_CONFIG_VERSION;
  range: ViewRange;
  /** Filter key → selected facet values. Empty selections are omitted. */
  filters: Record<string, string[]>;
  /** Filter chips visible in the toolbar (always-shown filters included). */
  shown_filters: string[];
  /** Nesting order AFTER the required Date group. */
  groups: string[];
  granularity: ReportGranularity;
  /** Visible metric keys, in display order (≥ 1). */
  columns: string[];
  /** Summary totals above the table (kpi_limits.min … kpi_limits.max). */
  kpis: string[];
  sort: ViewSort | null;
  /** Part of the saved-view contract (the API requires it). The UI always
   *  renders comfortable rows, so this is carried through but not shown. */
  density: ViewDensity;
}

/** The parts of a saved view every v2 layout has: date range and filters. */
export type BaseViewConfig = Pick<
  ViewConfig,
  "v" | "range" | "filters" | "shown_filters"
>;

/** A summary (ledger) report's saved view: just the date range and filters
 *  (the API rejects the table-only fields). */
export type LedgerViewConfig = BaseViewConfig;

/** Full catalog definition of a v2 report type (GET /report-definitions →
 *  `v2_definitions`). */
export interface ReportV2Definition extends ReportV2DefinitionSummary {
  date_range_presets: DateRangePreset[];
  granularities: Array<{ key: ReportGranularity; label: string }>;
  /** The Date group every table report starts with (null for a summary). */
  required_group: { key: string; label: string } | null;
  group_bys: ReportDimensionDef[];
  filters: ReportFilterDef[];
  metric_sections: ReportMetricSection[];
  /** Static metrics in display order. */
  metrics: ReportMetricDef[];
  dynamic_metric_groups: DynamicMetricGroup[];
  kpi_limits: { min: number; max: number };
  /** A summary's default view only carries the BaseViewConfig keys (range
   *  and filters); summary code never reads the table-only ones. */
  default_view: ViewConfig;
  /** Summary (ledger) reports only: their ledgers, without values. */
  ledgers?: ReportLedgerDef[];
}

/** Body of a run request. Omitted keys fall back to the saved view. */
export interface RunReportV2Request {
  range?: ViewRange;
  filters?: Record<string, string[]>;
  groups?: string[];
  granularity?: ReportGranularity;
}

/** One node of the result tree: depth 0 = date buckets (every bucket in the
 *  range, zero-filled, oldest first), then one level per group-by. */
export interface ReportTreeNodeV2 {
  /** "date" at depth 0, else the group-by key. */
  dimension: string;
  /** Bucket start date ("YYYY-MM-DD") or the group value ("" / "__none__"
   *  style keys for empty values). */
  key: string;
  /** Display label. Date buckets carry their start date; the portal formats
   *  it per granularity. */
  label: string;
  line_count: number;
  /** One value per `metric_keys` entry; null = unavailable / undefined. */
  values: Array<number | null>;
  children: ReportTreeNodeV2[];
}

export interface ReportFacetOption {
  value: string;
  label: string;
}

export interface RunReportV2Response {
  report: SalonReport;
  date_range: { preset: DateRangePreset; start: string; end: string };
  granularity: ReportGranularity;
  groups: string[];
  timezone: string;
  /** ISO-8601 timestamp the result was computed. */
  generated_at: string;
  /** Static metrics + this run's dynamic metrics, in display order. */
  metrics: ReportMetricDef[];
  /** Index order of every `values` array. */
  metric_keys: string[];
  totals: { line_count: number; values: Array<number | null> };
  nodes: ReportTreeNodeV2[];
  /** Filter options within the date range (ignores the other filters). */
  facets: Record<string, ReportFacetOption[]>;
}

/** One value column of a summary ledger (e.g. Payments / Refunds / Total). */
export interface ReportLedgerColumn {
  key: string;
  label: string;
  format: MetricFormat;
}

/** One ledger row; label / description / formula come verbatim from the
 *  reports service (reports_utils/v2/ledgers). */
export interface ReportLedgerRowDef {
  key: string;
  label: string;
  description: string;
  formula: string;
  /** Shown for information only and left out of the total row. */
  informational: boolean;
}

/** A ledger row's drill-down table (e.g. Earnings Summary → service
 *  commissions by service). Columns use the legacy report column shape. */
export interface ReportLedgerDrilldown {
  label: string;
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
}

export interface ReportLedgerRow extends ReportLedgerRowDef {
  /** One value per column. */
  values: number[];
  /** Optional drill-down; absent from older backends and most rows. */
  drilldown?: ReportLedgerDrilldown;
}

/** A summary ledger as listed in the catalog (rows without values; a ledger
 *  whose rows depend on the run, e.g. payment methods, lists none). */
export interface ReportLedgerDef {
  key: string;
  /** Heading of the label column ("Sales category"). */
  label: string;
  description: string;
  columns: ReportLedgerColumn[];
  rows: ReportLedgerRowDef[];
  /** Null when the ledger has no total row. */
  total_label: string | null;
}

/** A summary ledger with this run's values. */
export interface ReportLedger extends Omit<ReportLedgerDef, "rows"> {
  rows: ReportLedgerRow[];
  /** Adds the non-informational rows (null when the ledger has none). */
  total: { label: string; values: number[] } | null;
}

/** POST …/v2/run response for a summary (ledger) report. */
export interface RunLedgerReportResponse {
  report: SalonReport;
  date_range: { preset: DateRangePreset; start: string; end: string };
  timezone: string;
  generated_at: string;
  /** "own": the run only covers the caller (the staff filter is hidden). */
  scope?: "own" | "all";
  ledgers: ReportLedger[];
  /** Filter options within the date range (e.g. staff with earnings). */
  facets: Record<string, ReportFacetOption[]>;
}

/** Body of a summary run: only the range and filters apply. */
export type RunLedgerReportRequest = Pick<
  RunReportV2Request,
  "range" | "filters"
>;

/** The Date group a table report always starts with. */
export const DATE_GROUP = { key: "date", label: "Date" };

/** A table report's required first group (Date when the API omits it). */
export const requiredGroupOf = (
  definition: ReportV2Definition,
): { key: string; label: string } => definition.required_group ?? DATE_GROUP;

/** A summary (ledger) report, e.g. the Sales Summary. */
export const isLedgerDefinition = (
  definition: ReportV2DefinitionSummary | null | undefined,
): boolean => definition?.layout === "ledger";

/**
 * Narrow a catalog entry to a full v2 definition. A backend that predates the
 * canvas (or a malformed entry) may list only the summary fields.
 */
export function isFullV2Definition(
  value: ReportV2DefinitionSummary | null | undefined,
): value is ReportV2Definition {
  if (!value) return false;
  const d = value as Partial<ReportV2Definition>;
  return (
    Array.isArray(d.metrics) &&
    Array.isArray(d.metric_sections) &&
    Array.isArray(d.group_bys) &&
    Array.isArray(d.filters) &&
    Array.isArray(d.date_range_presets) &&
    Array.isArray(d.granularities) &&
    !!d.default_view &&
    !!d.kpi_limits
  );
}
